import { Color3, type Mesh, MeshBuilder, Quaternion, type Scene, StandardMaterial, TrailMesh, Vector3 } from "@babylonjs/core";
import { CONSTRUCTS, FX_COLORS as HERO_FX_COLORS, FxKind, PROJECTILES, type PredictedProjectile, type Vec3, ZONES, type BarrierSpec } from "@rivalwatch/shared";
import { HERO_FX, PROJECTILE_VIEWS, ZONE_VIEWS } from "../heroes/index.ts";
import type { ConstructFrame, ConstructView, FxCtx, FxView, ProjectileFrame, Rel, ZoneFrame } from "../heroes/types.ts";
import { glow, toon } from "./materials.ts";

interface Timed {
  mesh: Mesh;
  age: number;
  life: number;
  update?: (t: number) => void;
}

const BASE_FX_COLORS: Record<number, string> = {
  [FxKind.Rifle]: "#9ff3ff",
  [FxKind.Rocket]: "#ff8a3d",
  [FxKind.Pellet]: "#d7ccff",
  [FxKind.Pistol]: "#ffe680",
  [FxKind.Grenade]: "#3fd0ff",
  [FxKind.Spore]: "#7dff8a",
  [FxKind.Hammer]: "#ffd27a",
  [FxKind.Concussion]: "#ffd0a0",
  [FxKind.Hook]: "#b9a8ff",
  [FxKind.Missile]: "#ff5030",
  [FxKind.Dart]: "#c8ff9a",
  [FxKind.Shockwave]: "#e8b84a",
  [FxKind.Quake]: "#e8b84a",
  [FxKind.Singularity]: "#8a6cff",
  [FxKind.Charge]: "#ffffff",
};

const v = (p: Vec3) => new Vector3(p.x, p.y, p.z);
const fxColor = (kind: number, fallback = "#ffffff") => HERO_FX_COLORS[kind] ?? BASE_FX_COLORS[kind] ?? fallback;

type ProjView = { view?: FxView<unknown, ProjectileFrame>; mesh?: Mesh; trail?: TrailMesh; frame: ProjectileFrame };

export interface ProjectileSync {
  netId: number;
  kind: number;
  owner: number;
  pos: Vec3;
  vel: Vec3;
  rel: Rel;
}

/** Short-lived visual effects and replicated projectile/zone visuals. */
export class Fx {
  private timed: Timed[] = [];
  private projectiles = new Map<number, ProjView>();
  private predicted: { p: PredictedProjectile; e: ProjView; pos: Vector3; vel: Vector3; age: number }[] = [];
  private zones = new Map<number, { disc?: Mesh; dome?: Mesh; view?: FxView<unknown, ZoneFrame>; frame: ZoneFrame }>();
  private dt = 0;
  private constructs = new Map<number, { kind: number; generic?: { root: Mesh; hp: Mesh }; custom?: ConstructView }>();
  private barriers = new Map<number, Mesh>();
  private beams = new Map<number, Mesh>();

  constructor(private readonly scene: Scene) {}

  private ctx(rel: Rel): FxCtx {
    return {
      scene: this.scene,
      glow: (hex, a) => glow(this.scene, hex, a),
      toon: (hex) => toon(this.scene, hex),
      timed: (mesh, life, update) => {
        mesh.isPickable = false;
        this.timed.push({ mesh, age: 0, life, update: update ? (t) => update(t, mesh) : undefined });
      },
      rel,
    };
  }

  tracer(from: Vec3, to: Vec3, kind: number, rel: Rel = "none") {
    const hero = HERO_FX[kind];
    if (hero?.tracer) {
      hero.tracer(this.ctx(rel), from, to);
      if (!hero.keepGeneric) return;
    }
    const a = v(from);
    const b = v(to);
    const len = Vector3.Distance(a, b);
    if (len < 0.05) return;
    const m = MeshBuilder.CreateBox("tracer", { width: 0.035, height: 0.035, depth: 1 }, this.scene);
    m.material = glow(this.scene, fxColor(kind));
    m.position = Vector3.Lerp(a, b, 0.5);
    m.scaling.z = len;
    m.lookAt(b);
    m.isPickable = false;
    this.timed.push({ mesh: m, age: 0, life: kind === FxKind.Hook ? 0.25 : 0.09, update: (t) => (m.scaling.x = m.scaling.y = 1 - t) });
    this.impact(to, kind, 0.12, rel, !hero?.tracer);
  }

  impact(pos: Vec3, kind: number, size = 0.25, rel: Rel = "none", hooks = true) {
    const hero = hooks ? HERO_FX[kind] : undefined;
    if (hero?.impact) {
      hero.impact(this.ctx(rel), pos);
      if (!hero.keepGeneric) return;
    }
    const m = MeshBuilder.CreateSphere("spark", { diameter: 1, segments: 6 }, this.scene);
    m.material = glow(this.scene, fxColor(kind), 0.9);
    m.position = v(pos);
    m.isPickable = false;
    this.timed.push({ mesh: m, age: 0, life: 0.12, update: (t) => m.scaling.setAll(size * (1 + t * 2) * (1 - t * 0.5)) });
  }

  explosion(pos: Vec3, radius: number, kind: number, rel: Rel = "none") {
    const hero = HERO_FX[kind];
    if (hero?.explosion) {
      hero.explosion(this.ctx(rel), pos, radius);
      if (!hero.keepGeneric) return;
    }
    const color = fxColor(kind, "#ffaa55");
    const flat = kind === FxKind.Shockwave || kind === FxKind.Quake;
    const m = flat
      ? MeshBuilder.CreateTorus("ring", { diameter: 2, thickness: 0.15, tessellation: 32 }, this.scene)
      : MeshBuilder.CreateSphere("boom", { diameter: 2, segments: 12 }, this.scene);
    const mat = new StandardMaterial("boom", this.scene);
    mat.emissiveColor = Color3.FromHexString(color);
    mat.disableLighting = true;
    mat.alpha = 0.7;
    m.material = mat;
    m.position = v(pos);
    if (flat) m.position.y += 0.1;
    m.isPickable = false;
    this.timed.push({
      mesh: m,
      age: 0,
      life: flat ? 0.45 : 0.32,
      update: (t) => {
        const s = radius * (flat ? t : 0.35 + 0.65 * Math.sqrt(t));
        m.scaling.set(s, flat ? 1 : s, s);
        mat.alpha = 0.75 * (1 - t);
      },
    });
    const core = MeshBuilder.CreateSphere("core", { diameter: 1, segments: 8 }, this.scene);
    core.material = glow(this.scene, "#fff4d0");
    core.position = v(pos);
    core.isPickable = false;
    if (!flat) this.timed.push({ mesh: core, age: 0, life: 0.1, update: (t) => core.scaling.setAll(radius * 0.5 * (1 - t)) });
    else core.dispose();
  }

  pickup(pos: Vec3, large: boolean) {
    this.explosion({ x: pos.x, y: pos.y + 0.5, z: pos.z }, large ? 1.4 : 0.9, FxKind.Spore);
  }

  // ------------------------------------------------------------- projectiles

  private projectileView(frame: ProjectileFrame, name: string): ProjView {
    const build = PROJECTILE_VIEWS[frame.kind];
    if (build) return { view: build(this.scene, frame), frame };
    const vis = PROJECTILES[frame.kind]?.visual ?? { color: "#ffffff", size: 0.15 };
    const m = MeshBuilder.CreateSphere(name, { diameter: vis.size * 2, segments: 8 }, this.scene);
    m.material = glow(this.scene, vis.color);
    m.isPickable = false;
    m.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
    let trail: TrailMesh | undefined;
    if (vis.trail) {
      trail = new TrailMesh(`${name}-trail`, m, this.scene, vis.size * 0.7, 18, true);
      trail.material = glow(this.scene, vis.color, 0.45);
    }
    return { mesh: m, trail, frame };
  }

  private moveProjectile(e: ProjView, pos: Vec3, vel: Vec3) {
    const f = e.frame;
    const sp = Math.hypot(vel.x, vel.y, vel.z);
    f.pos = pos;
    if (sp > 1e-4) f.dir = { x: vel.x / sp, y: vel.y / sp, z: vel.z / sp };
    f.speed = sp;
    f.dt = this.dt;
    f.age += this.dt;
    if (e.view) e.view.update?.(e.view.gear, f);
    else e.mesh!.position.set(pos.x, pos.y, pos.z);
  }

  private disposeProjectile(e: ProjView) {
    e.view?.dispose();
    e.trail?.dispose();
    e.mesh?.dispose();
  }

  /** Sync server projectiles (interpolated). `hide` suppresses ones the local client predicted. */
  syncProjectiles(list: ProjectileSync[], hide: (netId: number) => boolean) {
    const seen = new Set<number>();
    for (const p of list) {
      if (hide(p.netId)) continue;
      seen.add(p.netId);
      let e = this.projectiles.get(p.netId);
      if (!e) {
        const sp = Math.hypot(p.vel.x, p.vel.y, p.vel.z) || 1;
        const frame: ProjectileFrame = { netId: p.netId, kind: p.kind, owner: p.owner, pos: p.pos, dir: { x: p.vel.x / sp, y: p.vel.y / sp, z: p.vel.z / sp }, speed: sp, age: 0, dt: 0, rel: p.rel, predicted: false };
        e = this.projectileView(frame, `proj${p.netId}`);
        this.projectiles.set(p.netId, e);
      }
      this.moveProjectile(e, p.pos, p.vel);
    }
    for (const [id, e] of this.projectiles) {
      if (seen.has(id)) continue;
      this.disposeProjectile(e);
      this.projectiles.delete(id);
    }
  }

  addPredicted(p: PredictedProjectile, owner = 0) {
    const sp = Math.hypot(p.vel.x, p.vel.y, p.vel.z) || 1;
    const frame: ProjectileFrame = { netId: 0, kind: p.kind, owner, pos: p.origin, dir: { x: p.vel.x / sp, y: p.vel.y / sp, z: p.vel.z / sp }, speed: sp, age: 0, dt: 0, rel: "self", predicted: true };
    const e = this.projectileView(frame, "pred");
    this.predicted.push({ p, e, pos: v(p.origin), vel: v(p.vel), age: 0 });
  }

  predictedSeqs(): Set<number> {
    return new Set(this.predicted.map((x) => x.p.seq));
  }

  /** Visual-only integration of locally predicted projectiles; `raycast` returns hit distance or -1. */
  private stepPredicted(dt: number, raycast: (o: Vec3, d: Vec3, max: number) => number) {
    for (let i = this.predicted.length - 1; i >= 0; i--) {
      const x = this.predicted[i]!;
      x.age += dt;
      x.vel.y -= (x.p.gravity ?? 0) * dt;
      const step = x.vel.scale(dt);
      const l = step.length();
      const dir = l > 0 ? step.scale(1 / l) : step;
      const hit = l > 0 ? raycast(x.pos, dir, l) : -1;
      if (hit >= 0 || x.age > x.p.seconds) {
        if (hit >= 0) this.impact(x.pos.add(dir.scale(hit)), 0, 0.3);
        this.disposeProjectile(x.e);
        this.predicted.splice(i, 1);
        continue;
      }
      x.pos.addInPlace(step);
      this.moveProjectile(x.e, { x: x.pos.x, y: x.pos.y, z: x.pos.z }, { x: x.vel.x, y: x.vel.y, z: x.vel.z });
    }
  }

  // ------------------------------------------------------------- zones, barriers, beams

  syncZones(list: { netId: number; kind: number; pos: Vec3; radius: number; ticks: number; rel: Rel }[]) {
    const seen = new Set<number>();
    for (const z of list) {
      seen.add(z.netId);
      let e = this.zones.get(z.netId);
      const vis = ZONES[z.kind]?.visual ?? { color: "#ffffff", dome: false };
      if (!e) {
        const frame: ZoneFrame = { netId: z.netId, kind: z.kind, pos: z.pos, radius: z.radius, ticks: z.ticks, age: 0, dt: 0, rel: z.rel };
        const build = ZONE_VIEWS[z.kind];
        if (build) e = { view: build(this.scene, frame), frame };
        else {
          const disc = MeshBuilder.CreateCylinder("zone", { diameter: 2, height: 0.06, tessellation: 40 }, this.scene);
          disc.material = glow(this.scene, vis.color, 0.28);
          disc.isPickable = false;
          let dome: Mesh | undefined;
          if (vis.dome) {
            dome = MeshBuilder.CreateSphere("dome", { diameter: 2, segments: 20, slice: 0.5 }, this.scene);
            dome.material = glow(this.scene, vis.color, 0.16);
            dome.isPickable = false;
          }
          e = { disc, dome, frame };
        }
        this.zones.set(z.netId, e);
      }
      const f = e.frame;
      f.pos = z.pos;
      f.radius = z.radius;
      f.ticks = z.ticks;
      f.rel = z.rel;
      f.dt = this.dt;
      f.age += this.dt;
      if (e.view) {
        e.view.update?.(e.view.gear, f);
        continue;
      }
      e.disc!.position.set(z.pos.x, z.pos.y + 0.04, z.pos.z);
      e.disc!.scaling.set(z.radius, 1, z.radius);
      if (e.dome) {
        e.dome.position.set(z.pos.x, z.pos.y, z.pos.z);
        const pulse = 1 + Math.sin(performance.now() / 120) * 0.02;
        e.dome.scaling.setAll(z.radius * pulse);
      }
    }
    for (const [id, e] of this.zones) {
      if (seen.has(id)) continue;
      e.view?.dispose();
      e.disc?.dispose();
      e.dome?.dispose();
      this.zones.delete(id);
    }
  }

  syncConstructs(list: (ConstructFrame & { build?: (scene: Scene, frame: ConstructFrame) => ConstructView })[]) {
    const seen = new Set<number>();
    for (const c of list) {
      seen.add(c.netId);
      const spec = CONSTRUCTS[c.kind];
      let e = this.constructs.get(c.netId);
      if (e && e.kind !== c.kind) {
        e.custom?.dispose();
        e.generic?.root.dispose();
        e.generic?.hp.dispose();
        this.constructs.delete(c.netId);
        e = undefined;
      }
      if (c.build) {
        if (!e?.custom) {
          e?.generic?.root.dispose();
          e?.generic?.hp.dispose();
          e = { kind: c.kind, custom: c.build(this.scene, c) };
          this.constructs.set(c.netId, e);
        }
        const custom = e.custom!;
        custom.update?.(custom.gear, c);
        continue;
      }
      const shape = spec?.visual.shape ?? spec?.shape.kind ?? "box";
      const color = spec?.visual.color ?? "#ffffff";
      if (!e?.generic) {
        e?.custom?.dispose();
        const root =
          shape === "cyl"
            ? MeshBuilder.CreateCylinder("construct", { diameter: (spec?.shape.kind === "cyl" ? spec.shape.r * 2 : 1), height: spec?.shape.h ?? 1, tessellation: 18 }, this.scene)
            : MeshBuilder.CreateBox("construct", {
                width: spec?.shape.kind === "box" ? spec.shape.hw * 2 : 1,
                depth: spec?.shape.kind === "box" ? spec.shape.hd * 2 : 1,
                height: spec?.shape.h ?? 1,
              }, this.scene);
        root.material = glow(this.scene, color, 0.55);
        root.isPickable = false;
        const hp = MeshBuilder.CreateBox("constructHp", { width: 1, height: 0.06, depth: 0.06 }, this.scene);
        hp.material = glow(this.scene, "#7dff8a", 0.9);
        hp.isPickable = false;
        e = { kind: c.kind, generic: { root, hp } };
        this.constructs.set(c.netId, e);
      }
      const generic = e.generic!;
      const specH = spec?.shape.h ?? 1;
      const mat = c.materialize ?? 1;
      generic.root.position.set(c.pos.x, c.pos.y + (specH * mat) / 2, c.pos.z);
      generic.root.rotation.y = c.yaw;
      generic.root.scaling.y = Math.max(0.02, mat);
      generic.root.visibility = 0.55 + 0.45 * c.hpFrac;
      generic.hp.position.set(c.pos.x, c.pos.y + specH + 0.18, c.pos.z);
      generic.hp.scaling.x = Math.max(0.02, c.hpFrac);
      generic.hp.setEnabled(c.hpFrac < 0.999);
    }
    for (const [id, e] of this.constructs) {
      if (seen.has(id)) continue;
      e.custom?.dispose();
      e.generic?.root.dispose();
      e.generic?.hp.dispose();
      this.constructs.delete(id);
    }
  }

  /** Anvil barriers: one per Anvil with the barrier raised. */
  syncBarriers(list: { netId: number; pos: Vec3; yaw: number; hpFrac: number; color: string; spec: BarrierSpec }[]) {
    const seen = new Set<number>();
    for (const b of list) {
      seen.add(b.netId);
      let m = this.barriers.get(b.netId);
      if (!m) {
        m = MeshBuilder.CreatePlane("barrier", { width: b.spec.width, height: b.spec.height, sideOrientation: 2 }, this.scene);
        m.material = glow(this.scene, b.color, 0.3);
        m.isPickable = false;
        m.enableEdgesRendering();
        m.edgesWidth = 3;
        m.edgesColor = Color3.FromHexString(b.color).toColor4(1);
        this.barriers.set(b.netId, m);
      }
      const fx = -Math.sin(b.yaw);
      const fz = -Math.cos(b.yaw);
      m.position.set(b.pos.x + fx * b.spec.forward, b.pos.y + b.spec.height / 2 - 0.2, b.pos.z + fz * b.spec.forward);
      m.rotation.y = b.yaw;
      m.visibility = 0.5 + 0.5 * b.hpFrac;
    }
    for (const [id, m] of this.barriers) {
      if (seen.has(id)) continue;
      m.dispose();
      this.barriers.delete(id);
    }
  }

  syncBeams(list: { id: number; from: Vec3; to: Vec3; color: string }[]) {
    const seen = new Set<number>();
    for (const b of list) {
      seen.add(b.id);
      let m = this.beams.get(b.id);
      if (!m) {
        m = MeshBuilder.CreateCylinder("beam", { diameter: 0.07, height: 1, tessellation: 6 }, this.scene);
        m.material = glow(this.scene, b.color, 0.85);
        m.isPickable = false;
        this.beams.set(b.id, m);
      }
      const a = v(b.from);
      const c = v(b.to);
      const len = Vector3.Distance(a, c);
      m.position = Vector3.Lerp(a, c, 0.5);
      m.scaling.set(1 + Math.sin(performance.now() / 60) * 0.3, len, 1);
      const dir = c.subtract(a).normalize();
      const axis = Vector3.Cross(Vector3.Up(), dir);
      const angle = Math.acos(Math.max(-1, Math.min(1, Vector3.Dot(Vector3.Up(), dir))));
      m.rotationQuaternion = axis.lengthSquared() > 1e-8 ? Quaternion.RotationAxis(axis.normalize(), angle) : null;
    }
    for (const [id, m] of this.beams) {
      if (seen.has(id)) continue;
      m.dispose();
      this.beams.delete(id);
    }
  }

  update(dt: number, raycast: (o: Vec3, d: Vec3, max: number) => number) {
    this.dt = dt;
    for (let i = this.timed.length - 1; i >= 0; i--) {
      const t = this.timed[i]!;
      t.age += dt;
      const k = Math.min(1, t.age / t.life);
      t.update?.(k);
      if (k >= 1) {
        t.mesh.dispose();
        this.timed.splice(i, 1);
      }
    }
    this.stepPredicted(dt, raycast);
  }

  dispose() {
    for (const t of this.timed) t.mesh.dispose();
    for (const e of this.projectiles.values()) this.disposeProjectile(e);
    for (const e of this.predicted) this.disposeProjectile(e.e);
    for (const e of this.zones.values()) {
      e.view?.dispose();
      e.disc?.dispose();
      e.dome?.dispose();
    }
    for (const e of this.constructs.values()) {
      e.custom?.dispose();
      e.generic?.root.dispose();
      e.generic?.hp.dispose();
    }
  }
}
